import { useState, useRef, useEffect } from 'react';

// T075 — Score badge with hover/focus popover.
//
// The badge shows the same compact number as before; on hover (or
// keyboard focus) it expands a popover with the per-dimension breakdown:
// rules + semantic + llm (when present), the human-readable reasons[]
// list, and the risks[] list. The data is already on every rec row
// (see backend/src/models/Recommendation.js), so this is a render-only
// change — no API churn.
//
// Variants:
//   `variant="dashboard"`   — large badge, used in the Available + table cells
//   `variant="compact"`     — small chip, used in Applications + Kanban side drawer
//
// If breakdown/reasons/risks are not passed, the badge degrades to its
// pre-T075 behavior: number only, no popover.

export default function ScoreBadge({
  score,
  breakdown,
  reasons,
  risks,
  missingSkills,
  variant = 'dashboard',
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside click — important when the user clicks/taps the badge
  // on touch devices, then clicks elsewhere.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const hasDetails =
    !!breakdown ||
    (Array.isArray(reasons) && reasons.length > 0) ||
    (Array.isArray(risks) && risks.length > 0) ||
    (Array.isArray(missingSkills) && missingSkills.length > 0);

  const cls = pickColors(score, variant);

  // Compact variant — small chip, used in tighter UIs.
  const badge = variant === 'compact'
    ? (
      <span className={'shrink-0 rounded border px-1.5 py-0.5 font-mono text-[10px] ' + cls}>{score ?? 0}</span>
    )
    : (
      <span className={'inline-block min-w-[2.5rem] rounded border px-2 py-0.5 text-center font-mono text-sm ' + cls}>{score ?? 0}</span>
    );

  if (!hasDetails) return badge;

  return (
    <span
      ref={wrapRef}
      className="relative inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="cursor-help"
        aria-label="Show score breakdown"
      >
        {badge}
      </button>
      {open && (
        <ScorePopover
          score={score}
          breakdown={breakdown}
          reasons={reasons}
          risks={risks}
          missingSkills={missingSkills}
        />
      )}
    </span>
  );
}

function ScorePopover({ score, breakdown, reasons, risks, missingSkills }) {
  // breakdown shape: { rules, semantic|null, llm|null }
  const rulesScore = breakdown?.rules ?? 0;
  const semanticScore = breakdown?.semantic;
  const negativeScore = breakdown?.negative; // T058 — points deducted (penalty)
  const showSemantic = semanticScore !== null && semanticScore !== undefined;
  // T353 — mirrors combineScore: the blend runs only with a semantic score AND
  // a positive rules score. Anywhere else the total IS the rules number and no
  // weight was applied to it.
  const blended = showSemantic && Number(rulesScore) > 0;
  const showNegative = typeof negativeScore === 'number' && negativeScore > 0;

  return (
    <div
      role="tooltip"
      className="absolute right-0 top-full z-30 mt-2 w-72 rounded-lg border border-slate-700 bg-slate-900/95 p-3 text-left text-xs text-slate-200 shadow-xl backdrop-blur"
    >
      <div className="flex items-center justify-between border-b border-slate-800 pb-2">
        <p className="font-semibold text-slate-100">Score breakdown</p>
        <span className="font-mono text-base text-brand-400">{score ?? 0}</span>
      </div>

      {/* T319 — the popover listed three numbers that could not produce the
          fourth. A real row reads "66" at the top and "56" and "81" beneath it,
          and nothing on screen says the total is 0.6 x rules + 0.4 x semantic.
          The arithmetic was always right; it was simply unverifiable, which for
          a breakdown is the whole job. Showing the weights is the fix. */}
      {/* T353 — the weights are shown only when they are the ones used.
          `combineScore` returns `rules` UNWEIGHTED in two cases: no semantic
          score yet, and rules <= 0 (T315's branch, which refuses to let a
          cosine rescue a negative). The popover printed "x 0.6" regardless, so
          in exactly those cases the arithmetic it displayed did not produce the
          header above it — 40 with "40 x 0.6" beside it, which is 24. Showing
          the weights was T319's whole point; showing them when they do not
          apply is the same failure in the other direction. */}
      <dl className="mt-2 space-y-1">
        <Row
          label="Rules (location, skills, salary…)"
          value={rulesScore}
          active
          weight={blended ? 'x 0.6' : undefined}
        />
        {showSemantic ? (
          <Row
            label="Semantic (profile ↔ job)"
            value={semanticScore}
            active
            weight={blended ? 'x 0.4' : undefined}
            note={blended ? undefined : 'not blended — a negative rules score is not rescued by similarity'}
          />
        ) : (
          <Row label="Semantic (profile ↔ job)" value="—" muted note="computed on next scan — the total is rules-only until then" />
        )}
        {/* T290 — "AI fit signal" is gone. `scoreBreakdown.llm` is written by
            NOTHING: 0 of 10,208 recommendations carry a value, and T181 already
            established why when it removed the same component from the analytics
            card — "a vestige of the original three-layer design… the LLM layer
            produces explanation bullets, not a number, and combineScore blends
            only rules + semantic minus the negative penalty".
            
            This surface was the one T181 missed, and it was worse than an empty
            component: it named a dimension of the score that does not exist and
            told the operator "run Explain to compute" it. Running Explain writes
            `llmExplanation` — 328 rows have one — and never touches this field,
            so the instruction could not be followed. */}
        {showNegative && (
          <Row
            label="Dismissed-similar penalty"
            value={`−${negativeScore}`}
            active
            note="similar to a role you dismissed"
          />
        )}
      </dl>

      {Array.isArray(reasons) && reasons.length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-widest text-emerald-400">Why it matched</p>
          <ul className="mt-1 space-y-0.5">
            {reasons.slice(0, 6).map((r, i) => (
              <li key={i} className="flex items-start gap-1 text-emerald-200">
                <span className="shrink-0" aria-hidden="true">✓</span><span className="leading-snug">{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(risks) && risks.length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-widest text-amber-400">Risks / gaps</p>
          <ul className="mt-1 space-y-0.5">
            {risks.slice(0, 6).map((r, i) => (
              <li key={i} className="flex items-start gap-1 text-amber-200">
                <span className="shrink-0" aria-hidden="true">⚠</span><span className="leading-snug">{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(missingSkills) && missingSkills.length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] uppercase tracking-widest text-slate-400">Missing skills</p>
          <p className="mt-1 leading-snug text-slate-300">{missingSkills.slice(0, 8).join(', ')}{missingSkills.length > 8 ? ` +${missingSkills.length - 8}` : ''}</p>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, muted, active, note, weight }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className={muted ? 'text-slate-500' : 'text-slate-300'}>
        {label}
        {note && <span className="block text-xs text-slate-500">{note}</span>}
      </dt>
      <dd className={'flex shrink-0 items-baseline gap-1 font-mono ' + (active ? 'text-slate-100' : 'text-slate-500')}>
        <span>{value}</span>
        {weight && <span className="text-[10px] text-slate-500">{weight}</span>}
      </dd>
    </div>
  );
}

function pickColors(score, variant) {
  if (variant === 'compact') {
    return score >= 60
      ? 'border-emerald-700 text-emerald-300 bg-emerald-950/30'
      : score >= 30
        ? 'border-brand-700 text-brand-500 bg-brand-600/15'
        : 'border-slate-700 text-slate-400 bg-slate-900/50';
  }
  return score >= 60
    ? 'bg-emerald-950/60 text-emerald-300 border-emerald-800'
    : score >= 30
      ? 'bg-brand-600/15 text-brand-500 border-brand-700'
      : score >= 0
        ? 'bg-slate-900 text-slate-300 border-slate-700'
        : 'bg-red-950/40 text-red-400 border-red-800';
}
